import type { QueryClient } from '@tanstack/react-query';
import { deleteCrossAppCacheByPrefix, deleteCrossAppCacheKeys } from '@/utils/cross-app-cache';
import { authKeys } from '@/services/auth/query-keys';

/**
 * Refresh the shell's cached account after a save — the Hydra-native equivalent
 * of the Ember `announceRefreshAuth()` handoff.
 *
 * The cross-app account payloads are dropped first, for the reason
 * `invalidateAuthenticatedUser` documents: the `['authenticated']` query fn is
 * `fetchCurrentAccount()`, which answers from that cache before it reaches the
 * network, so the invalidation alone is a no-op for up to ~10s and repopulates
 * the store from the pre-save snapshot. Surfaces that read the account from the
 * auth store — the billing contact card among them — would otherwise keep
 * showing the old values until a reload, behind a success snackbar.
 */
export function refreshAuthenticatedAccount(queryClient: QueryClient, accountId: number): void {
  // Exact keys, not a `/accounts` prefix bust: that prefix also covers the billing and
  // role-definition caches, which an account save has no reason to discard.
  deleteCrossAppCacheKeys('/accounts/current', `/accounts/${accountId}`);
  deleteCrossAppCacheByPrefix('/account_by_environment');
  void queryClient.invalidateQueries({ queryKey: authKeys.authenticated() });
}
